/**
 * Which beats get a chapter card, as data — split out of `chapterCard.ts` so it
 * can be read by things that must never load a DOM class.
 *
 * `ChapterCard` builds `HTMLDivElement`/`SVGPathElement` nodes in its
 * constructor, so `chapterCard.ts` is loaded through the dev server in its own
 * spec (`tests/e2e/chapter-card.spec.ts`) rather than imported at the top of a
 * file, by that spec's own comment. `src/story/campaignValidation.ts` needs the
 * same *decision* — does beat N have a card, and if not, was that decided or
 * forgotten — from the sim lane, which runs in plain Node with no `document`.
 * Rather than gamble on whether importing `chapterCard.ts` for its lookup alone
 * stays side-effect-free forever, the decision table lives here, where nothing
 * above it can ever require a browser.
 *
 * This is the same two-derivations lesson `beats.ts`'s own header names: one
 * table, read by both the UI and the validator, instead of two lists of the
 * same fact drifting apart.
 */

/** A mark's id. Must match a key actually drawn in `chapterCard.ts`'s `MARKS`. */
export type ChapterMarkId =
  | 'padlock' | 'sheaf' | 'paw' | 'hook' | 'tally'
  | 'barb' | 'bulb' | 'shears' | 'moon'
  | 'glasses' | 'tag' | 'bell';

/**
 * Whether a beat has a card. `'none'` carries a reason so a beat with no card
 * reads as a decision, not a gap — `docs/engine-architecture-audit.md` A2 asks
 * for exactly this distinction, and does not permit inventing the missing
 * artwork to close it.
 */
export type ChapterCardDecision =
  | { readonly kind: 'mark'; readonly mark: ChapterMarkId }
  | { readonly kind: 'none'; readonly reason: string };

/**
 * One entry per beat that has a predecessor (some other beat's `next` names
 * it) — beats 1 and 0 are exempt for reasons `campaignValidation.ts` states,
 * not because they are missing here.
 *
 * Beats 7-10 were `'none'` for four sessions, which is the gap D-085 was built
 * to close and then never extended to cover: four hand-overs in a row arriving
 * on a cut to black. They are drawn now (D-195; five marks redrawn for
 * legibility by D-236) and the reasoning for each mark is beside its path data
 * in `chapterCard.ts`, where it can be read next to the thing it is about.
 */
export const CHAPTER_CARD_DECISIONS: Readonly<Record<number, ChapterCardDecision>> = {
  2: { kind: 'mark', mark: 'padlock' },
  3: { kind: 'mark', mark: 'sheaf' },
  4: { kind: 'mark', mark: 'paw' },
  5: { kind: 'mark', mark: 'hook' },
  6: { kind: 'mark', mark: 'tally' },
  7: { kind: 'mark', mark: 'barb' },
  8: { kind: 'mark', mark: 'bulb' },
  9: { kind: 'mark', mark: 'shears' },
  10: { kind: 'mark', mark: 'moon' },
  11: { kind: 'mark', mark: 'glasses' },
  12: { kind: 'mark', mark: 'tag' },
  // Beat 12 runs directly into the failure; a card would falsely make the
  // ambush feel like a fresh chapter instead of the plan collapsing in place.
  13: { kind: 'none', reason: 'The dawn-yard hand-over must remain unbroken.' },
  14: { kind: 'mark', mark: 'bell' },
};

/** The raw decision for a beat, or `undefined` when nobody has decided yet. */
export function chapterCardDecisionForBeat(beat: number): ChapterCardDecision | undefined {
  return CHAPTER_CARD_DECISIONS[beat];
}

/** Whether a beat has a card, and which mark it is. Beat 1 opens the game cold. */
export function chapterMarkForBeat(beat: number): ChapterMarkId | null {
  const decision = CHAPTER_CARD_DECISIONS[beat];
  return decision?.kind === 'mark' ? decision.mark : null;
}
